import { useNavigate } from 'hooks';
import { Flex } from 'components/flex';
import { List } from 'components/list';
import { useAppSelector } from 'store';
import { formatDate } from 'utils/date';
import { Avatar } from 'components/avatar';
import { formatPrice } from 'utils/helpers';
import { Messages, AppRoutes } from 'config';
import { ListCard } from 'components/list-card';
import { ListHeader } from 'components/list-header';
import { DeleteDialog } from 'components/delete-dialog';
import { canEditCompletedLtl } from 'components/stock-ltl-edit/config';
import { isAuthCompanyUser, selectAuthUserRole } from 'store/slices/auth';
import { LazyStockTransferLtlEdit } from 'components/stock-ltl-edit/lazy';
import { transferDefaultEventId } from 'components/stock-transfer-event-split/config';
import {
  useRemoveMutation,
  StockTransferStatus,
} from 'store/api/stock-transfers';

import * as Styles from './styles';
import { StockTransferDetailsProps } from './types';

const titles = ['Name', 'Count', 'Price', 'Total price'];

export const StockTransferDetails = ({
  data,
  isOnlyRead,
}: StockTransferDetailsProps) => {
  const navigate = useNavigate();
  const [remove, mutation] = useRemoveMutation();
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);
  const role = useAppSelector(selectAuthUserRole);
  const isCompleted = data.status === StockTransferStatus.Completed;
  const canDelete = !isOnlyRead && (isCompanyOwner || !isCompleted);
  // L4a: on a Completed transfer the LTL invoice # / price stay editable for
  // the Company Owner and Administrative staff (hidden while Main lacks the route)
  const canEditLtl = !isOnlyRead && isCompleted && canEditCompletedLtl(role);

  return (
    <Styles.StyledGeneralInfo
      withoutPhoto
      withPlaceholders
      stickyWithHeader={false}
      disableSticky={isOnlyRead}
      withoutPadding={isOnlyRead}
      name={`Stock transfer ${data.id}`}
      onEdit={!canDelete ? undefined : () => navigate('edit')}
      rightSlot={
        !canDelete ? undefined : (
          <DeleteDialog
            id={data.id}
            action={remove}
            mutation={mutation}
            name="stock transfer"
            title="Delete Stock Transfer"
            navigateUrl={AppRoutes.Inventory.Transfers.Home}
            successMessage={`Stock transfer ${Messages.DELETED_SUCCESSFULLY}`}
          />
        )
      }
      sections={[
        {
          title: 'General info',
          rightSlot: canEditLtl ? (
            <LazyStockTransferLtlEdit
              id={data.id}
              stored={{
                ltlPrice: data.ltlPrice,
                workOrder: data.workOrder,
                trackingNumber: data.trackingNumber,
                ltlInvoiceNumber: data.ltlInvoiceNumber,
                carrierId: data.carrierId ?? data.carrier?.id,
              }}
              transfer={{
                type: data.type,
                eventAllocations: data.eventAllocations,
                defaultEventId: transferDefaultEventId({
                  type: data.type,
                  sourceEventId: data.sourceStorage?.eventId,
                  destinationEventId: data.destinationStorage?.eventId,
                }),
              }}
            />
          ) : undefined,
          rows: [
            [
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'Date',
                value: formatDate(data.date),
              },
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'Status',
                value: data.status,
              },
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'Wholesale cost',
                value: formatPrice(data.wholesaleCost),
              },
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'LTL price',
                value: formatPrice(data.ltlPrice),
              },
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'LTL invoice #',
                value: data.ltlInvoiceNumber || '-',
              },
              // Owner 2026-10-16: the carrier's work order, as on a stock
              // order.
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'Work Order',
                value: data.workOrder || '-',
              },
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'Carrier',
                value: data.carrier?.name || '-',
              },
              // Owner 2026-10-19: the carrier's tracking #, as on a stock
              // order.
              {
                col: 3,
                colTablet: 3,
                colMobile: 6,
                title: 'Tracking #',
                value: data.trackingNumber || '-',
              },
              {
                col: 3,
                colTablet: 3,
                title: 'Created By',
                value: data.createdBy?.name,
              },
              // T-L09 (L14) / owner 2026-10-16: the events the transfer's
              // freight is for, each with its percent and share.
              ...(data.eventAllocations?.length
                ? [
                    {
                      col: 3,
                      colTablet: 3,
                      title: 'For events',
                      // one line per event: name and share
                      value: (
                        <>
                          {data.eventAllocations.map((row) => (
                            <div key={row.eventId}>
                              {row.event?.name ?? 'Event'} {Number(row.percent)}
                              % · {formatPrice(row.amount)}
                            </div>
                          ))}
                        </>
                      ),
                    },
                  ]
                : []),
            ],
          ],
        },
        {
          title: 'Source storage',
          rows: [
            [
              {
                col: 4,
                colTablet: 4,
                title: 'Name',
                value: data.sourceStorage?.name,
              },
              {
                col: 4,
                colTablet: 4,
                title: 'Address',
                value: data.sourceStorage?.address,
              },
              {
                col: 4,
                colTablet: 4,
                title: 'Phone',
                value: data.sourceStorage?.phone,
              },
              {
                col: 4,
                colTablet: 4,
                title: 'Contact name',
                value: data.sourceStorage?.contactName,
              },
              {
                col: 4,
                colTablet: 4,
                title: 'Emergency Phone',
                value: data.sourceStorage?.emergencyPhone,
              },
            ],
          ],
        },
        ...(data.destinationCompany
          ? [
              {
                title: 'Destination company',
                rows: [
                  [
                    {
                      col: 4,
                      colTablet: 4,
                      title: 'Name',
                      value: data.destinationCompany.name,
                    },
                  ],
                ],
              },
            ]
          : []),
        ...(data.destinationStorage
          ? [
              {
                title: 'Destination storage',
                rows: [
                  [
                    {
                      col: 4,
                      colTablet: 4,
                      title: 'Name',
                      value: data.destinationStorage.name,
                    },
                    {
                      col: 4,
                      colTablet: 4,
                      title: 'Address',
                      value: data.destinationStorage.address,
                    },
                    {
                      col: 4,
                      colTablet: 4,
                      title: 'Phone',
                      value: data.destinationStorage.phone,
                    },
                    {
                      col: 4,
                      colTablet: 4,
                      title: 'Contact name',
                      value: data.destinationStorage?.contactName,
                    },
                    {
                      col: 4,
                      colTablet: 4,
                      title: 'Emergency Phone',
                      value: data.destinationStorage?.emergencyPhone,
                    },
                  ],
                ],
              },
            ]
          : []),
        {
          title: 'Products',
          children: (
            <List
              count={data.items.length}
              style={{ marginBottom: '32px' }}
              mainWrapperStyles={{ padding: 0 }}
              header={<ListHeader padding="8px" titles={titles} />}
            >
              {data.items.map((item) => (
                <ListCard
                  headers={titles}
                  key={item.productId}
                  values={[
                    <Flex gap={8} alignItems="center">
                      <Avatar url={item.product.photoUrl} />
                      <h3>{item.product.name}</h3>
                    </Flex>,
                    item.count,
                    formatPrice(item.price),
                    formatPrice(item.count * item.price),
                  ]}
                />
              ))}

              <ListCard
                withPlaceholders={false}
                values={[
                  <h3>Total: </h3>,
                  '',
                  <h3>{formatPrice(data.wholesaleCost)}</h3>,
                ]}
              />
            </List>
          ),
        },
      ]}
    />
  );
};
